import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Chip, Popover, Typography, useTheme } from '@mui/material';
import { formatDate, formatUserName } from '../../utils/formatUtils';
import {
    getBookLanguageChipColor,
    getBookLanguageStageLabel,
    getChipColor,
    getChipLabel,
    getProcessStageLabel,
} from '../../utils/processStageUtils';

/**
 * Shared language popover component for showing language details
 *
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the popover is open
 * @param {HTMLElement} props.anchorEl The element to anchor the popover to
 * @param {Function} props.onClose Handler for when the popover is closed
 * @param {Array} props.languages List of languages with their status
 * @param {Array} props.videoLanguages Full video languages data with update information
 */
const LanguagePopover = ({ open, anchorEl, onClose, languages, videoLanguages = [], isBookFile = false }) => {
    const theme = useTheme();
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <Box sx={{ p: 2, maxWidth: 350 }}>
                <Typography variant="subtitle2" gutterBottom>
                    All Languages
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {isBookFile
                        ? Object.entries(languages).map(([langName, langStatus]) => {
                              const processStage = langStatus?.processStage;
                              return (
                                  <Box
                                      key={langName}
                                      sx={{ mb: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                                  >
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                          <Chip
                                              label={langName}
                                              size="small"
                                              sx={{
                                                  mr: 1,
                                                  fontSize: '0.7rem',
                                                  backgroundColor: `${getBookLanguageChipColor(processStage, theme.palette.mode)}20`,
                                                  color: getBookLanguageChipColor(processStage, theme.palette.mode),
                                                  borderColor: getBookLanguageChipColor(processStage, theme.palette.mode),
                                                  border: '1px solid',
                                                  fontWeight: 500,
                                              }}
                                          />
                                          <Typography variant="body2">
                                              {getBookLanguageStageLabel(processStage)}
                                          </Typography>
                                      </Box>

                                      {langStatus?.exportedAt && (
                                          <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>
                                              <AccessTimeFilledIcon
                                                  sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }}
                                              />
                                              <Typography variant="caption" color="text.secondary">
                                                  Exported: {formatDate(langStatus.exportedAt)}
                                              </Typography>
                                          </Box>
                                      )}
                                  </Box>
                              );
                          })
                        : Array.isArray(languages)
                        ? languages.map((lang) => {
                              // Find detailed language info from videoLanguages
                              const langDetails = videoLanguages?.find((vl) => vl.languageName === lang.name);

                              return (
                                  <Box
                                      key={lang.name}
                                      sx={{
                                          mb: 1.5,
                                          pb: 1.5,
                                          borderBottom: '1px solid',
                                          borderColor: 'divider',
                                          '&:last-child': {
                                              borderBottom: 'none',
                                              mb: 0,
                                              pb: 0,
                                          },
                                      }}
                                  >
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                          <Chip
                                              label={getChipLabel(lang.name, lang.processStage)}
                                              size="small"
                                              sx={{
                                                  mr: 1,
                                                  fontSize: '0.7rem',
                                                  backgroundColor: `${getChipColor(lang.processStage, theme.palette.mode)}20`,
                                                  color: getChipColor(lang.processStage, theme.palette.mode),
                                                  borderColor: getChipColor(lang.processStage, theme.palette.mode),
                                                  border: '1px solid',
                                                  fontWeight: 500,
                                              }}
                                          />
                                          <Typography variant="body2">
                                              {getProcessStageLabel(lang.processStage)}
                                          </Typography>
                                      </Box>

                                      {langDetails && (
                                          <Box sx={{ pl: 1 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                  <PersonIcon
                                                      sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }}
                                                  />
                                                  <Typography variant="caption" color="text.secondary">
                                                      Last updated by:{' '}
                                                      {langDetails.lastModifiedBy ||
                                                          formatUserName(langDetails.updatedBy) ||
                                                          'Unknown'}
                                                  </Typography>
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <AccessTimeFilledIcon
                                                      sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }}
                                                  />
                                                  <Typography variant="caption" color="text.secondary">
                                                      {langDetails.lastModifiedDate
                                                          ? formatDate(langDetails.lastModifiedDate)
                                                          : 'Unknown date'}
                                                  </Typography>
                                              </Box>
                                          </Box>
                                      )}
                                  </Box>
                              );
                          })
                        : // Text file translations display
                          Object.entries(languages).map(([langName, langData]) => {
                              const status = langData.translationStatus || 'UNKNOWN';
                              const statusLabel =
                                  status === 'COMPLETED'
                                      ? 'Ready'
                                      : status === 'IN_PROGRESS'
                                      ? 'Generating'
                                      : status === 'PENDING'
                                      ? 'Pending'
                                      : status === 'SOURCE_LANGUAGE'
                                      ? 'Source Language'
                                      : 'Unknown';
                              const statusColor =
                                  status === 'COMPLETED'
                                      ? '#f57c00'
                                      : status === 'SOURCE_LANGUAGE'
                                      ? '#2e7d32'
                                      : '#757575';

                              return (
                                  <Box
                                      key={langName}
                                      sx={{ mb: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                                  >
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                          <Chip
                                              label={langName}
                                              size="small"
                                              sx={{
                                                  mr: 1,
                                                  fontSize: '0.7rem',
                                                  backgroundColor:
                                                      status === 'COMPLETED'
                                                          ? 'rgba(255, 193, 7, 0.2)'
                                                          : 'rgba(158, 158, 158, 0.2)',
                                                  color: statusColor,
                                                  borderColor: statusColor,
                                                  border: '1px solid',
                                                  fontWeight: 500,
                                              }}
                                          />
                                          <Typography variant="body2">{statusLabel}</Typography>
                                      </Box>

                                      {langData.lastUpdatedBy && (
                                          <Box sx={{ pl: 1 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                  <PersonIcon
                                                      sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }}
                                                  />
                                                  <Typography variant="caption" color="text.secondary">
                                                      Updated by: {formatUserName(langData.lastUpdatedBy)}
                                                  </Typography>
                                              </Box>

                                              {langData.lastUpdatedAt && (
                                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                      <AccessTimeFilledIcon
                                                          sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }}
                                                      />
                                                      <Typography variant="caption" color="text.secondary">
                                                          {formatDate(langData.lastUpdatedAt)}
                                                      </Typography>
                                                  </Box>
                                              )}
                                          </Box>
                                      )}
                                  </Box>
                              );
                          })}
                </Box>
            </Box>
        </Popover>
    );
};

export default LanguagePopover;
